import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { logError } from "@/lib/log";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { FileText, CheckCircle2 } from "lucide-react";
import PackFlickthrough from "@/components/PackFlickthrough";
import { sectorForLookupKey } from "@/lib/packVideo";

export default function PackPreviewDialog({ resource, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!resource) { setData(null); return; }
    api.get(`/resources/${resource.id}/policy-preview`).then((r) => setData(r.data)).catch((e) => logError("pack-preview", e));
  }, [resource]);
  const s = data?.sample;
  const videoSector = sectorForLookupKey(resource?.lookup_key);
  return (
    <Dialog open={!!resource} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="pack-preview-dialog">
        <DialogHeader>
          <DialogTitle>{resource?.title}</DialogTitle>
          <DialogDescription>{data ? `${data.count} full policies · ${data.formats.join(" + ")} · editable in-app after purchase` : "Loading…"}</DialogDescription>
        </DialogHeader>
        {videoSector && <PackFlickthrough sectorId={videoSector} className="mt-2" />}
        {data && (
          <div className="space-y-6 text-sm">
            <section>
              <h3 className="font-display font-semibold mb-2">Every policy includes</h3>
              <div className="flex flex-wrap gap-1.5">{data.sections.map((x) => <Badge key={x} variant="secondary">{x}</Badge>)}</div>
            </section>
            <section>
              <h3 className="font-display font-semibold mb-2">What's inside ({data.count})</h3>
              <ul className="grid sm:grid-cols-2 gap-1" data-testid="pack-preview-list">
                {data.policies.map((p) => <li key={p.title} className="flex items-center gap-1.5 text-xs"><FileText className="w-3 h-3 text-muted-foreground shrink-0" /> {p.title}</li>)}
              </ul>
            </section>
            {s && (
              <section className="rounded-xl border border-border bg-muted/30 p-4" data-testid="pack-preview-sample">
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Sample extract</p>
                <h3 className="font-display font-semibold mb-2">{s.title}</h3>
                {s.purpose.map((p, i) => <p key={i} className="mb-2">{p}</p>)}
                <p className="font-medium mt-3 mb-1">Legal and regulatory basis</p>
                <ul className="list-disc pl-5 space-y-0.5">{s.legal_basis.map((x, i) => <li key={i}>{x}</li>)}</ul>
                {s.procedure.map((sub, i) => (
                  <div key={i}><p className="font-medium mt-3 mb-1">Procedure — {sub.heading}</p><ol className="list-decimal pl-5 space-y-0.5">{sub.steps.map((x, j) => <li key={j}>{x}</li>)}</ol></div>
                ))}
                <p className="font-medium mt-3 mb-1">What inspectors will look for</p>
                <ul className="space-y-0.5">{s.inspector_focus.map((x, i) => <li key={i} className="flex gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" /> {x}</li>)}</ul>
                <p className="text-xs text-muted-foreground mt-3">…continues for the full policy. Each runs to several pages.</p>
              </section>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
